import axios, { AxiosError, AxiosResponse } from 'axios'
import { API_CONFIG } from '@/config/api'
import { AppError, ErrorCode, ValidationError } from './error'
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  setAuthSession,
} from './auth'

const isDevelopment =
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'development'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuth?: boolean
    _retry?: boolean
  }

  interface InternalAxiosRequestConfig {
    skipAuth?: boolean
    _retry?: boolean
  }
}

// 创建 Axios 实例
const http = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    ...API_CONFIG.DEFAULT_HEADERS,
  },
  withCredentials: true,
})

const AUTH_WHITE_LIST = ['/auth/login', '/auth/refresh']

function isAuthWhiteList(url?: string): boolean {
  if (!url) return false
  return AUTH_WHITE_LIST.some((path) => url.includes(path))
}

/**
 * 根据 HTTP 状态码映射错误码
 */
function mapStatusToErrorCode(status: number): ErrorCode {
  const mapping: Record<number, ErrorCode> = {
    400: ErrorCode.BAD_REQUEST,
    401: ErrorCode.UNAUTHORIZED,
    403: ErrorCode.FORBIDDEN,
    404: ErrorCode.NOT_FOUND,
    409: ErrorCode.CONFLICT,
    422: ErrorCode.VALIDATION_ERROR,
  }
  if (status >= 500) return ErrorCode.SERVER_ERROR
  return mapping[status] || ErrorCode.UNKNOWN
}

/**
 * 从响应数据中提取校验错误
 */
function extractValidationErrors(data: any): ValidationError[] | undefined {
  // 适配后端返回格式：{ errors: [{ field: 'name', message: '不能为空' }] }
  if (data?.errors && Array.isArray(data.errors)) {
    return data.errors.map((e: any) => ({
      field: e.field || e.property || 'unknown',
      message: e.message || e.constraints?.[Object.keys(e.constraints)[0]] || '校验失败',
    }))
  }
  return undefined
}

/**
 * 将任意错误转换为 AppError
 */
function toAppError(error: unknown): AppError {
  // 已经是 AppError
  if (error instanceof AppError) return error

  // Axios 错误
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>
    
    // 网络错误（无响应）
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return new AppError(ErrorCode.TIMEOUT, undefined, { originalError: error })
      }
      return new AppError(ErrorCode.NETWORK_ERROR, undefined, { originalError: error })
    }

    const { status, data } = axiosError.response
    const code = mapStatusToErrorCode(status)
    const message = data?.message || data?.error || axiosError.message
    const validationErrors = code === ErrorCode.VALIDATION_ERROR 
      ? extractValidationErrors(data) 
      : undefined

    return new AppError(code, message, { status, validationErrors, originalError: error })
  }

  // 普通 Error
  if (error instanceof Error) {
    return new AppError(ErrorCode.UNKNOWN, error.message, { originalError: error })
  }

  // 其他
  return new AppError(ErrorCode.UNKNOWN, String(error), { originalError: error })
}

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response.data
    
    // 业务错误：HTTP 2xx 但 success: false
    if (data && typeof data === 'object' && data.success === false) {
      throw new AppError(
        ErrorCode.BUSINESS_ERROR,
        data.message || '操作失败',
        { status: response.status, originalError: response }
      )
    }

    // 统一响应包装：{ success, data, message, requestId }
    if (data && typeof data === 'object' && 'success' in data && data.success === true) {
      return {
        ...response,
        data: 'data' in data ? data.data : data,
      }
    }
    
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config
    const status = error.response?.status

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthWhiteList(originalRequest.url)
    ) {
      const currentRefreshToken = getRefreshToken()
      if (currentRefreshToken) {
        originalRequest._retry = true

        try {
          const refreshResponse = await http.post<{
            accessToken: string
            refreshToken: string
          }>(
            '/auth/refresh',
            { refreshToken: currentRefreshToken },
            { skipAuth: true }
          )

          const { accessToken, refreshToken } = refreshResponse.data as {
            accessToken: string
            refreshToken: string
          }
          setAuthSession({
            accessToken,
            refreshToken,
          })

          originalRequest.headers = originalRequest.headers ?? {}
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return http(originalRequest)
        } catch {
          clearAuthSession()
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          return Promise.reject(new AppError(ErrorCode.UNAUTHORIZED))
        }
      }
    }

    const appError = toAppError(error)
    
    // 认证错误：跳转登录页
    if (appError.isAuthError) {
      clearAuthSession()
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    
    // 开发环境打印详细错误
    if (isDevelopment) {
      console.error('[AppError]', {
        code: appError.code,
        message: appError.message,
        status: appError.status,
        validationErrors: appError.validationErrors,
      })
    }
    
    return Promise.reject(appError)
  }
)

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    const token = getAccessToken()
    const shouldAttachToken = token && !config.skipAuth && !isAuthWhiteList(config.url)
    if (shouldAttachToken) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(toAppError(error))
  }
)

export default http
